import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import * as XLSX from 'xlsx';

export default function ImportNumerosReservesPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [previewData, setPreviewData] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ inserted: number; skipped: number; errors: string[] } | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressDone, setProgressDone] = useState(0);
  const [stats, setStats] = useState<{ total: number; disponibles: number; attribues: number } | null>(null);
  const [numerosDisponibles, setNumerosDisponibles] = useState<string[]>([]);
  const [numerosDisponiblesLoading, setNumerosDisponiblesLoading] = useState(false);
  const [showAllNumeros, setShowAllNumeros] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadStats();
    loadNumerosDisponibles();
  }, []);

  const loadStats = async () => {
    try {
      const { count: total } = await supabase
        .from('numeros_reserves')
        .select('*', { count: 'exact', head: true });

      const { count: disponibles } = await supabase
        .from('numeros_reserves')
        .select('*', { count: 'exact', head: true })
        .eq('statut', 'disponible');

      const { count: attribues } = await supabase
        .from('numeros_reserves')
        .select('*', { count: 'exact', head: true })
        .eq('statut', 'attribué');

      setStats({
        total: total || 0,
        disponibles: disponibles || 0,
        attribues: attribues || 0,
      });
    } catch {
      // ignore
    }
  };

  const loadNumerosDisponibles = async () => {
    setNumerosDisponiblesLoading(true);
    try {
      const limit = showAllNumeros ? 1000 : 100;
      const { data, error } = await supabase
        .from('numeros_reserves')
        .select('numero')
        .eq('statut', 'disponible')
        .limit(limit);

      if (error) { setNumerosDisponiblesLoading(false); return; }
      // Tri numérique : le plus petit numéro d'abord (1393, 1575, 1830, ...)
      const sorted = (data || []).map((r: { numero: string }) => r.numero).sort((a, b) => {
        const numA = parseInt(a, 10);
        const numB = parseInt(b, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      });
      setNumerosDisponibles(sorted);
    } catch {
      // ignore
    } finally {
      setNumerosDisponiblesLoading(false);
    }
  };

  const toggleShowAllNumeros = () => {
    setShowAllNumeros(!showAllNumeros);
    setTimeout(() => {
      const limit = !showAllNumeros ? 1000 : 100;
      supabase
        .from('numeros_reserves')
        .select('numero')
        .eq('statut', 'disponible')
        .limit(limit)
        .then(({ data }) => {
          const sorted = (data || []).map((r: { numero: string }) => r.numero).sort((a, b) => {
            const numA = parseInt(a, 10);
            const numB = parseInt(b, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return a.localeCompare(b);
          });
          setNumerosDisponibles(sorted);
        });
    }, 0);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user || (role !== 'super_admin')) {
    navigate('/', { replace: true });
    return null;
  }

  const parseXlsx = (data: Uint8Array): string[] => {
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    // Lire toutes les valeurs de la colonne A
    const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { header: 1 });

    const numeros: string[] = [];
    for (const row of jsonData) {
      if (!row || (Array.isArray(row) && row.length === 0)) continue;

      let cellValue: unknown;
      if (Array.isArray(row)) {
        cellValue = row[0];
      } else {
        const firstKey = Object.keys(row)[0];
        cellValue = row[firstKey];
      }

      if (cellValue === undefined || cellValue === null || cellValue === '') continue;

      let numStr = String(cellValue).trim();

      // Détecter si c'est un en-tête
      const headerKeywords = ['numéro', 'numero', 'n°', 'id', 'identification', 'nombre', 'number', 'liste'];
      if (headerKeywords.some((kw) => numStr.toLowerCase().includes(kw))) {
        continue;
      }

      // Nettoyer : enlever les décimales .0 si c'est un nombre Excel
      if (numStr.match(/^\d+\.0+$/)) {
        numStr = numStr.replace(/\.0+$/, '');
      }

      if (numStr) {
        numeros.push(numStr);
      }
    }

    return numeros;
  };

  const handleFile = (file: File) => {
    setFileName(file.name);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const parsed = parseXlsx(data);
        setPreviewData(parsed);
      } catch {
        setErrorMsg('Erreur lors de la lecture du fichier. Vérifiez le format XLSX.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      handleFile(file);
    } else {
      setErrorMsg('Veuillez déposer un fichier Excel (.xlsx)');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleImport = async () => {
    if (previewData.length === 0) return;
    setImporting(true);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');

    const CHUNK_SIZE = 5000;
    const chunks: string[][] = [];
    for (let i = 0; i < previewData.length; i += CHUNK_SIZE) {
      chunks.push(previewData.slice(i, i + CHUNK_SIZE));
    }

    setProgressTotal(chunks.length);
    setProgressDone(0);

    const totalResult = { inserted: 0, skipped: 0, errors: [] as string[] };

    try {
      const { data: { session } } = await supabase.auth.getSession();

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/import-numeros-reserves`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ numeros: chunk }),
          }
        );

        const result = await resp.json();

        if (result.success) {
          totalResult.inserted += result.inserted || 0;
          totalResult.skipped += result.skipped_duplicates || 0;
          if (result.errors && result.errors.length > 0) {
            totalResult.errors.push(...result.errors);
          }
        } else {
          totalResult.errors.push(`Lot ${i + 1}/${chunks.length} : ${result.error || 'Erreur inconnue'}`);
        }

        setProgressDone(i + 1);
        setImportResult({ ...totalResult });
      }

      setSuccessMsg(
        `Import terminé : ${totalResult.inserted} insérés, ${totalResult.skipped} ignorés (${chunks.length} lot${chunks.length > 1 ? 's' : ''})`
      );
      loadStats();
      loadNumerosDisponibles();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'import.');
    } finally {
      setImporting(false);
    }
  };

  const clearFile = () => {
    setFileName('');
    setPreviewData([]);
    setImportResult(null);
    setSuccessMsg('');
    setErrorMsg('');
    setProgressTotal(0);
    setProgressDone(0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Import Numéros Réservés</h1>
              <p className="mt-2 text-gray-600">
                Importez la liste des numéros d'identification libres depuis un fichier Excel (.xlsx). Colonne A = numéro.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour admin
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{errorMsg}</span>
          </div>
        )}
        {successMsg && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl flex items-start gap-2">
            <i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{successMsg}</span>
          </div>
        )}

        {/* Stats */}
        {stats && stats.total > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 text-center">
              <p className="text-3xl font-bold text-gray-800">{stats.total.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-1">Total importés</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-5 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.disponibles.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-1">Disponibles</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-5 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.attribues.toLocaleString()}</p>
              <p className="text-sm text-gray-500 mt-1">Attribués</p>
            </div>
          </div>
        )}

        {/* Numéros disponibles en clair */}
        {stats && stats.disponibles > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-green-600"></i>
                Numéros disponibles
                <span className="text-sm font-normal text-gray-500">
                  ({stats.disponibles.toLocaleString()} au total)
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleShowAllNumeros}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`ri-${showAllNumeros ? 'arrow-up-s' : 'arrow-down-s'}-line`}></i>
                  {showAllNumeros ? 'Réduire' : 'Afficher plus'}
                </button>
                <button
                  onClick={loadNumerosDisponibles}
                  disabled={numerosDisponiblesLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`ri-refresh-line ${numerosDisponiblesLoading ? 'animate-spin' : ''}`}></i>
                  Actualiser
                </button>
              </div>
            </div>

            {numerosDisponiblesLoading ? (
              <div className="text-center py-8 text-gray-400">
                <i className="ri-loader-4-line animate-spin text-2xl mb-2 block"></i>
                <span className="text-sm">Chargement...</span>
              </div>
            ) : numerosDisponibles.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <span className="text-sm">Aucun numéro disponible.</span>
              </div>
            ) : (
              <>
                <div className="max-h-96 overflow-x-auto overflow-y-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-sm min-w-[420px]">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs w-16">#</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Numéro brut</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs">Format CSF</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {numerosDisponibles.map((num, i) => (
                        <tr key={num} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-700">{num}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium font-mono">
                              CSF{num}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!showAllNumeros && stats.disponibles > 100 && (
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Affichage des 100 premiers numéros. Cliquez "Afficher plus" pour voir jusqu'à 1 000 numéros.
                  </p>
                )}
                {showAllNumeros && stats.disponibles > 1000 && (
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Affichage limité aux 1 000 premiers numéros (sur {stats.disponibles.toLocaleString()} disponibles).
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Zone de dépôt */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-excel-2-line text-3xl text-green-600"></i>
            </div>
            {fileName ? (
              <>
                <p className="text-lg font-semibold text-gray-900">{fileName}</p>
                <p className="text-sm text-gray-500 mt-1">{previewData.length.toLocaleString()} numéros détectés</p>
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  className="mt-3 text-sm text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>Supprimer
                </button>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold text-gray-700 mb-1">
                  Glissez-déposez votre fichier Excel ici
                </p>
                <p className="text-sm text-gray-500">
                  ou cliquez pour parcourir (.xlsx)
                </p>
              </>
            )}
          </div>

          {/* Format attendu */}
          {!fileName && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-2xl">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-blue-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Format attendu :</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li><strong>Colonne A</strong> : Numéro d'identification (un par ligne)</li>
                    <li>Les en-têtes sont automatiquement détectés et ignorés</li>
                    <li>Les doublons (déjà présents en base) sont automatiquement ignorés</li>
                  </ul>
                  <p className="mt-2 text-blue-600">
                    Ces numéros seront consommés en priorité lors de la création de nouveaux patients.
                    Une fois tous les numéros réservés épuisés, la numérotation séquentielle reprendra à partir de <strong>117891</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Aperçu */}
        {previewData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">
              Aperçu ({previewData.length.toLocaleString()} numéros)
            </h3>
            <div className="max-h-80 overflow-x-auto overflow-y-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm min-w-[420px]">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700 w-16">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Numéro</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Format CSF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {previewData.slice(0, 100).map((num, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{num}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium font-mono">
                          CSF{num}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {previewData.length > 100 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-center text-gray-500 text-sm">
                        ... et {(previewData.length - 100).toLocaleString()} autres numéros
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Barre de progression */}
            {importing && progressTotal > 1 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progression : lot {progressDone}/{progressTotal}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round((progressDone / progressTotal) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-3 bg-green-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.round((progressDone / progressTotal) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={importing}
              className="mt-6 w-full px-6 py-3.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {importing ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Importation en cours... ({progressDone}/{progressTotal})
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-2-line"></i>
                  Importer {previewData.length.toLocaleString()} numéros
                </>
              )}
            </button>
          </div>
        )}

        {/* Résultat */}
        {importResult && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-bold text-gray-800 mb-4 text-lg">Résultat de l'import</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-3xl font-bold text-green-600">{importResult.inserted.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mt-1">Insérés</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-3xl font-bold text-gray-600">{importResult.skipped.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mt-1">Ignorés (doublons)</p>
              </div>
            </div>
            {importResult.errors.length > 0 && (
              <div className="p-4 bg-red-50 rounded-xl border border-red-200 max-h-48 overflow-y-auto">
                <p className="text-sm font-semibold text-red-700 mb-2">Erreurs :</p>
                {importResult.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Explication du fonctionnement */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
          <h3 className="font-bold text-gray-800 mb-4 text-lg flex items-center gap-2">
            <i className="ri-lightbulb-line text-amber-500"></i>
            Comment ça marche
          </h3>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-700 font-bold text-xs">1</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Import de la liste</p>
                <p>Vous importez ici tous les anciens numéros libres. Ils sont stockés avec le statut "disponible".</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-700 font-bold text-xs">2</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Attribution prioritaire</p>
                <p>Quand un nouveau patient est créé, le système prend automatiquement le plus petit numéro disponible dans cette liste et le marque comme "attribué".</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-700 font-bold text-xs">3</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Numérotation séquentielle</p>
                <p>Une fois tous les numéros réservés épuisés, le système reprend la numérotation normale à partir de <strong>117891</strong> sans jamais réutiliser un numéro déjà présent dans cette table.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}